import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// import ReactDOM from "react-dom";

import { surfacingConfig, fallHeightOptions, mulchConfig, ewfConfig, turfConfig } from '../quote/constants';
import { useOutsideAlerter } from '../quote/utils';
import { setCookie } from '../utils/utils';
// import { AddToQuote } from "../quote/components/addToQuote";
// import { QuickAddToQuote } from '../quote/components/addToQuoteItem';
import { CloseIcon } from '../utils/icons';

const PIP_PRODUCT_HANDLE = surfacingConfig.surfacing_handle;
const PIP_PRODUCT_URL = surfacingConfig.surfacing_url;
const TURF_PRODUCT_HANDLE = turfConfig.turf_handle;
const MULCH_PRODUCT_HANDLE = mulchConfig.ground_smart_handle;
const MULCH_VOLUME = mulchConfig.ground_smart_weight / mulchConfig.mulch_volume;
const EWF_PRODUCT_URL = ewfConfig.ewf_url;

const SURFACING_AVAILABLE_HEIGHTS = Object.keys(surfacingConfig.surfacing_tiers).map((key) =>
  parseInt(key.replace('p_', '').replace('_ft', ''), 10)
);

const LOWER_RANGE_MIN_VARIANCE = 0.1125;
const LOWER_RANGE_MAX_VARIANCE = 0.145;
const UPPER_RANGE_MIN_VARIANCE = 0.165;
const UPPER_RANGE_MAX_VARIANCE = 0.1925;

const getSurfacingTier = (height) => {
  const tierKey = `p_${height}_ft`;
  if (surfacingConfig.surfacing_tiers[tierKey]) {
    return surfacingConfig.surfacing_tiers[tierKey];
  }
  const closestHeight = SURFACING_AVAILABLE_HEIGHTS.reduce((prev, curr) =>
    Math.abs(curr - height) < Math.abs(prev - height) ? curr : prev
  );
  return surfacingConfig.surfacing_tiers[`p_${closestHeight}_ft`] || 0;
};

const getRandomVariance = (squareFootage = null) => {
  let lowerMin = LOWER_RANGE_MIN_VARIANCE;
  let lowerMax = LOWER_RANGE_MAX_VARIANCE;
  let upperMin = UPPER_RANGE_MIN_VARIANCE;
  let upperMax = UPPER_RANGE_MAX_VARIANCE;

  if (squareFootage && squareFootage >= 3000) {
    lowerMin = LOWER_RANGE_MIN_VARIANCE / 1.5;
    lowerMax = LOWER_RANGE_MAX_VARIANCE / 1.5;
    upperMin = UPPER_RANGE_MIN_VARIANCE / 1.5;
    upperMax = UPPER_RANGE_MAX_VARIANCE / 1.5;
  }

  return {
    lower: Math.random() * (lowerMax - lowerMin) + lowerMin,
    upper: Math.random() * (upperMax - upperMin) + upperMin,
  };
};

const roundToNearestThousand = (price) => {
  if (price >= 250000) {
    return Math.round(price / 20000) * 20000;
  }
  if (price >= 50000) {
    return Math.round(price / 5000) * 5000;
  }
  return Math.round(price / 1000) * 1000;
};

const getMulchHeight = (height) => {
  if (height >= 3 && height <= 5) return 3;
  if (height >= 6 && height <= 7) return 4;
  if (height >= 8 && height <= 10) return 5;
  if (height >= 11 && height <= 12) return 6;
  if (height >= 13 && height <= 14) return 7;
  return 0;
};

const getTurfPrice = (height, sqf) => {
  if (height <= 5) {
    if (sqf <= 749) return 24;
    if (sqf <= 999) return 22.75;
    if (sqf <= 1249) return 21.5;
    if (sqf <= 1499) return 20.5;
    if (sqf <= 2000) return 19;
    if (sqf <= 3000) return 18;
    if (sqf >= 3000) return 18;
  } else if (height > 5 && height <= 10) {
    if (sqf <= 749) return 25.25;
    if (sqf <= 999) return 24;
    if (sqf <= 1249) return 22.7;
    if (sqf <= 1499) return 21.75;
    if (sqf <= 2000) return 20.25;
    if (sqf <= 3000) return 19.25;
    if (sqf >= 3000) return 18.25;
  }
  return 0;
};

const computeCalculatorResults = ({ squareFootage, fallHeight, currentVariance, mulchPrice }) => {
  if (!(squareFootage > 0 && fallHeight > 0 && parseFloat(squareFootage) >= 10)) {
    return null;
  }

  const sqf = parseFloat(squareFootage);
  const height = parseFloat(fallHeight);
  const selectedTier = getSurfacingTier(height);

  if (selectedTier === 0) return null;

  const baseCost = sqf * selectedTier;
  const pip_v = Math.round(baseCost * surfacingConfig.p_margin);
  const minPIPPrice = Math.round(pip_v * (1 - currentVariance.lower));
  const maxPIPPrice = Math.round(pip_v * (1 + currentVariance.upper));

  const pipPriceRange = {
    pip_v,
    minPIPPrice: roundToNearestThousand(minPIPPrice),
    maxPIPPrice: roundToNearestThousand(maxPIPPrice),
  };

  let mulchAmount;
  let numMulchBags;
  let numMulchBagsWithExtraBags;
  let mulchTotalPrice = null;

  if (mulchPrice > 0) {
    const mulchHeight = getMulchHeight(height);

    if (mulchHeight > 0) {
      const calculatedMulchAmount = sqf * (mulchHeight / 12) * (mulchConfig.ground_smart_weight / MULCH_VOLUME);
      const mulchBags = Math.ceil(calculatedMulchAmount / mulchConfig.ground_smart_weight);

      const calculatedMulchAmountWithExtraBags =
        sqf * ((mulchHeight + 1) / 12) * (mulchConfig.ground_smart_weight / MULCH_VOLUME);
      let mulchBagsWithExtraBags = Math.ceil(calculatedMulchAmountWithExtraBags / mulchConfig.ground_smart_weight);
      if (mulchBags === mulchBagsWithExtraBags) {
        mulchBagsWithExtraBags += 1;
      }

      mulchAmount = Math.ceil(calculatedMulchAmount);
      numMulchBags = mulchBags;
      numMulchBagsWithExtraBags = mulchBagsWithExtraBags;
      mulchTotalPrice = {
        mulchTotalPrice: mulchPrice * mulchBags,
        minMulchTotalPrice: mulchPrice * mulchBags,
        maxMulchTotalPrice: mulchPrice * mulchBagsWithExtraBags,
      };
    }
  }

  let EWFCubicYards = 0;
  let EWFTotalPriceRange = null;

  if (ewfConfig.ewf_min_price && ewfConfig.ewf_max_price) {
    const ewfHeight = height <= 4 ? 8 : 12;
    const ewfTierKey = `e_${ewfHeight}_ft`;
    const compactionFactor = ewfConfig.ewf_compaction_factor[ewfTierKey] || 6;
    const calcCubicFeet = sqf * (ewfHeight / 12);
    const calcCubicYards = calcCubicFeet * compactionFactor;

    EWFCubicYards = calcCubicYards;
    EWFTotalPriceRange = {
      totalPrice: Math.ceil((calcCubicYards * ((ewfConfig.ewf_min_price + ewfConfig.ewf_max_price) / 2)) * ewfConfig.ewf_margin),
      minEWFTotalPrice: Math.ceil((calcCubicYards * ewfConfig.ewf_min_price) * ewfConfig.ewf_margin),
      maxEWFTotalPrice: Math.ceil((calcCubicYards * ewfConfig.ewf_max_price) * ewfConfig.ewf_margin),
    };
  }

  let turfTotalPriceRange = null;
  const turfPrice = getTurfPrice(height, sqf);

  if (turfPrice > 0) {
    const turfTotalCost = turfPrice * sqf;
    const turfTotalPrice = turfTotalCost * turfConfig.t_margin + turfTotalCost;
    turfTotalPriceRange = {
      turfTotalCost: Math.ceil(turfTotalCost),
      turfTotalPrice: Math.ceil(turfTotalPrice),
      minTurfTotalPrice: Math.ceil(roundToNearestThousand(turfTotalPrice * (1 - currentVariance.lower))),
      maxTurfTotalPrice: Math.ceil(roundToNearestThousand(turfTotalPrice * (1 + currentVariance.upper))),
    };
  }

  return {
    pipPriceRange,
    mulchAmount,
    numMulchBags,
    numMulchBagsWithExtraBags,
    mulchTotalPrice,
    EWFCubicYards,
    EWFTotalPriceRange,
    turfTotalPriceRange,
  };
};

// export const initializePIPCalculatorModal  = () => {
//   if (document.getElementById('pip-calculator-button-container')) {
//     ReactDOM.render(
//       React.createElement(PIPCalculatorModalButton),
//       document.getElementById('pip-calculator-button-container')
//     );
//   };
// }


export const PIPCalculatorModalButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pip_calculator_open',
      value: 1
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="pip-calculator-button">
      <a onClick={openModal} className="button button--outline">Estimate Surfacing Costs</a>
        {/* <button onClick={openModal} className="button button--primary button--outline">Estimate Pour in Place Costs</button> */}
        {isModalOpen && <PipCalculatorModal onClose={closeModal} isOpen={isModalOpen} />}
      </div>
    </>
  );
};

/*
export const AddToQuoteButton = ({ buttonURL, onClose, fallHeight, squareFootage }) => {
  if (buttonURL) {
    return (
      <a href={buttonURL} className="button button--primary button--small">Add to Quote</a>
    );
  }
  return (
    <>
      <button className="button button--primary button--small"
        // disabled={resultsAreStale}
        onClick={() => {
          onClose();
          const fallHeightElement = document.getElementById('critical_fall_height-1-0') || document.getElementById('critical_fall_height-0-0');
          if (fallHeightElement) {
            const optionValue = `${fallHeight} Feet`;
            const option = Array.from(fallHeightElement.options).find(opt => opt.value === optionValue);
            if (option) {
              option.selected = true;
              fallHeightElement.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          const squareFootageElement = document.getElementById('square_footage-2-0') || document.getElementById('square_footage-1-0');
          if (squareFootageElement) {
            squareFootageElement.value = squareFootage;
            squareFootageElement.focus();
          }
        }}>
          Add to Quote
        </button>

    </>
  );
}
*/



export async function getGroundSmartPrice() {
  const res = await fetch(mulchConfig.ground_smart_product_url+'.json', { cache: 'no-store' });
  if (!res.ok) throw new Error('Unable to load product JSON');

  const { product } = await res.json();
  const firstVariant = product?.variants?.[0];
  if (!firstVariant) throw new Error('No variants found');
  // console.log('firstVariant', firstVariant, firstVariant.price);
  return {
    groundSmartPrice: Number(firstVariant.price),
    // mulchProductId: product?.id,
    // mulchVariantId: firstVariant.id,
    mulchTitle: product?.title,

    mulchURL: `https://willygoat.com/products/${product?.handle}?variant=${firstVariant.id}`,
    // mulchFeaturedImage: product?.images?.[0]?.src,
    // mulchVariantTitle: firstVariant.title,
  };
}


const PipCalculatorModal = ({ onClose, isOpen }) => {
  const [squareFootage, setSquareFootage] = useState('');
  const [fallHeight, setFallHeight] = useState('');
  const [currentVariance, setCurrentVariance] = useState({ lower: 0, upper: 0 });
  const [showSquareFootageError, setShowSquareFootageError] = useState(false);

  const [mulchPrice, setMulchPrice] = useState(0);
  const [mulchTitle, setMulchTitle] = useState(null);
  const [mulchProductURL, setMulchProductURL] = useState(mulchConfig.ground_smart_product_url);

  const modalRef = useRef(null);
  const lastAnalyticsKeyRef = useRef(null);
  useOutsideAlerter(modalRef, isOpen, onClose);

  const productPageFlags = useMemo(() => {
    const pathname = window.location.pathname;
    return {
      pip: pathname.includes(`/products/${PIP_PRODUCT_HANDLE}`),
      turf: pathname.includes(`/products/${TURF_PRODUCT_HANDLE}`),
      mulch: pathname.includes(`/products/${MULCH_PRODUCT_HANDLE}`),
      ewf: pathname.includes(`/products/${ewfConfig.ewf_url}`),
    };
  }, []);

  const updateVariance = useCallback((sqfOverride = null) => {
    const sqf = sqfOverride ?? (parseFloat(squareFootage) || null);
    setCurrentVariance(getRandomVariance(sqf));
  }, [squareFootage]);

  useEffect(() => {
    setCurrentVariance(getRandomVariance());
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchGroundSmartData = async () => {
      try {
        const { groundSmartPrice, mulchURL, mulchTitle: title } = await getGroundSmartPrice();
        if (cancelled) return;

        setMulchTitle(title);
        setMulchProductURL(mulchURL);
        setMulchPrice(groundSmartPrice);
      } catch (error) {
        if (!cancelled) {
          console.error('Error getting ground smart price:', error);
        }
      }
    };

    fetchGroundSmartData();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const calculatorResults = useMemo(
    () => computeCalculatorResults({ squareFootage, fallHeight, currentVariance, mulchPrice }),
    [squareFootage, fallHeight, currentVariance, mulchPrice]
  );

  const {
    pipPriceRange,
    mulchTotalPrice,
    EWFTotalPriceRange,
    turfTotalPriceRange,
  } = calculatorResults || {};

  const showResults = Boolean(calculatorResults);

  useEffect(() => {
    if (!calculatorResults?.pipPriceRange) return;

    const { pip_v, minPIPPrice, maxPIPPrice } = calculatorResults.pipPriceRange;
    const analyticsKey = `${squareFootage}-${fallHeight}-${pip_v}-${minPIPPrice}-${maxPIPPrice}`;

    if (lastAnalyticsKeyRef.current === analyticsKey) return;
    lastAnalyticsKeyRef.current = analyticsKey;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pip_calculator_calculate',
      value: pip_v,
      pip_value: pip_v,
      square_footage: squareFootage,
      fall_height: fallHeight,
      min_price: minPIPPrice,
      max_price: maxPIPPrice,
    });

    setCookie('used_pip_calculator', 'true', 30);
  }, [calculatorResults, squareFootage, fallHeight]);

  const handleAddToQuote = useCallback(() => {
    onClose();
    const fallHeightElement = document.getElementById('critical_fall_height-1-0') || document.getElementById('critical_fall_height-0-0');
    if (fallHeightElement) {
      const optionValue = `${fallHeight} Feet`;
      const option = Array.from(fallHeightElement.options).find((opt) => opt.value === optionValue);
      if (option) {
        option.selected = true;
        fallHeightElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const squareFootageElement = document.getElementById('square_footage-2-0') || document.getElementById('square_footage-1-0');
    if (squareFootageElement) {
      squareFootageElement.value = squareFootage;
      squareFootageElement.focus();
    }
  }, [onClose, fallHeight, squareFootage]);

  return (

    <div id="pip-calculator-modal" className="modal" aria-hidden={!isOpen} onClick={onClose}>
      <div className="modal__dialog" role="dialog" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h3 className="modal__title heading h2">Surfacing Calculator</h3>
          <button className="modal__close link" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </header>

        <div className="modal__content">
          <div className="rte calculator-wrapper">
            <div className="form__input-row">

              <div className="form__input-wrapper params-wrapper">
                <label className="control-label" htmlFor="squareFootage">Square Footage:</label>
                <input className={`form-control input-md focus:ring-2 ${showSquareFootageError && squareFootage && parseFloat(squareFootage) < 10 ? 'input-error' : ''}`} type="number" id="squareFootage" value={squareFootage}
                  onChange={(e) => {
                      setSquareFootage(e.target.value);
                  }}
                  onBlur={() => {
                      setShowSquareFootageError(true);
                      updateVariance(parseFloat(squareFootage) || null);
                  }}
                  placeholder="Enter square footage" min="10"
                />

              </div>

              <div className="form__input-wrapper params-wrapper" >
                <label className="control-label fallHeight-label" htmlFor="fallHeight">Critical Fall Height (ft)</label>
                <div className="select-wrapper select-wrapper--primary fallHeight-wrapper" >
                  <svg focusable="false" className="icon icon--arrow-bottom" viewBox="0 0 12 8" role="presentation">
                    <path stroke="currentColor" strokeWidth="2" d="M10 2L6 6 2 2" fill="none" strokeLinecap="round"></path>
                  </svg>
                    <select id="fallHeight" value={fallHeight} onChange={(e) => {
                      setFallHeight(e.target.value);
                      updateVariance();
                  }} className="form-control">
                      <option value="">Select fall height</option>
                      {fallHeightOptions.map(height => (
                        <option key={height} value={height}>{height} ft</option>
                      ))}
                    </select>
                </div>
              </div>


            </div>

            {showSquareFootageError && squareFootage && parseFloat(squareFootage) < 10 && (
              <span className="error-message">Minimum 10 square feet required</span>
            )}



            {showResults && pipPriceRange && (
              // <div className={`answer-container ${resultsAreStale ? 'stale-results' : ''}`}>
              <div className={`answer-container`}>

                <h4 className="heading h4 estimated-heading">Estimated Price Range: </h4>

                {/* <p className={`results-value`}>${pipPriceRange.minPIPPrice.toLocaleString()} - ${pipPriceRange.maxPIPPrice.toLocaleString()}</p> */}

                <table className="pip-calculator-table">
                  <tbody>

                    {pipPriceRange && (
                      <>
                      <tr>
                        <td>
                          <h4 className="heading h4">
                            {productPageFlags.pip ? (
                              'Pour in Place Surfacing'
                            ) : (
                              <a href={PIP_PRODUCT_URL} className="link" target="_blank" rel="noopener noreferrer">Pour in Place Surfacing</a>
                            )}
                            <br />
                            <span className="subheading">(Fully Installed)</span>
                          </h4>
                        </td>
                        <td className="results-value">${pipPriceRange.minPIPPrice.toLocaleString()} - ${pipPriceRange.maxPIPPrice.toLocaleString()}</td>
                        <td className="action-button-wrapper">
                          {productPageFlags.pip ? (
                            <button className="button button--primary button--small"
                            onClick={handleAddToQuote}>
                              Add to Quote
                            </button>
                          ) : (
                            <a href={PIP_PRODUCT_URL} className="button button--primary button--small">View Product</a>
                          )}
                        </td>
                      </tr>
                      </>
                    )}

                    {fallHeight <= 11 && turfTotalPriceRange?.minTurfTotalPrice && (
                      <>
                      <tr>
                        <td>
                          <h4 className="heading h4">
                            {productPageFlags.turf ? (
                              'Playground Turf'
                            ) : (
                              <a href={turfConfig.turf_url} className="link" target="_blank" rel="noopener noreferrer">Playground Turf</a>
                            )}
                            <br />
                            <span className="subheading">(Fully Installed)</span>
                          </h4>
                        </td>
                        <td className="results-value">${turfTotalPriceRange.minTurfTotalPrice.toLocaleString()} - ${turfTotalPriceRange.maxTurfTotalPrice.toLocaleString()}</td>
                        <td className="action-button-wrapper">
                          {productPageFlags.turf ? (
                            <button className="button button--primary button--small"
                            onClick={handleAddToQuote}>
                              Add to Quote
                            </button>
                          ) : (
                            <a href={turfConfig.turf_url} className="button button--primary button--small">Shop Turf</a>
                          )}
                        </td>
                      </tr>
                      </>
                    )}

                    {mulchTotalPrice && (
                      <>
                        <tr>
                          <td>
                            {/* <h4 className="heading h4">{mulchTitle} <br /> */}
                            <h4 className="heading h4">
                              {productPageFlags.mulch ? (
                                'Playground Rubber Mulch'
                              ) : (
                                <a href={mulchProductURL} className="link" target="_blank" rel="noopener noreferrer">Playground Rubber Mulch</a>
                              )}
                              <br />
                              {/* <span className="subheading">{numMulchBags} - {numMulchBagsWithExtraBags} bags - ({mulchAmount} lbs.)</span> */}
                              <span className="subheading">(Excluding Freight)</span>
                            </h4>
                          </td>
                          <td className="results-value">
                            ${mulchTotalPrice.mulchTotalPrice.toLocaleString()} - ${mulchTotalPrice.maxMulchTotalPrice.toLocaleString()} <br />

                          </td>
                          <td className="action-button-wrapper">
                            <a href={mulchProductURL} target="_blank" className="button button--primary button--small">Shop Mulch</a>
                          {/* <QuickAddToQuote
                            // disabled={resultsAreStale}
                            quantity={numMulchBags}
                            productId={mulchProductId}
                            variantId={mulchVariantId}
                            price={mulchPrice * 100}
                            title={mulchTitle}
                            variantTitle={mulchVariantTitle}
                            featuredImage={mulchFeaturedImage}
                            url={mulchProductURL}
                            vendor={mulchVendor}
                            options={[{key:"_LeadTime",label:"Lead Time",value:"1 to 2 Weeks"}]}
                          /> */}
                          </td>
                        </tr>
                      </>
                    )}

                    {EWFTotalPriceRange?.totalPrice && (
                      <>
                        <tr>
                          <td>
                            <h4 className="heading h4">
                              {productPageFlags.ewf ? (
                                ewfConfig.ewf_title
                              ) : (
                                <a href={ewfConfig.ewf_url} className="link" target="_blank" rel="noopener noreferrer">{ewfConfig.ewf_title}</a>
                              )}
                            <br />
                            <span className="subheading">(Excluding Freight)</span>
                              {/* <span className="subheading">({Math.ceil(EWFCubicYards)} cubic yards)</span> */}
                            </h4>
                          </td>
                          <td className="results-value">
                            ${EWFTotalPriceRange.minEWFTotalPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })} - ${EWFTotalPriceRange.maxEWFTotalPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })} <br />
                            {/* <span className="subheading">Not applicable in California</span> */}
                            {/* <span className="subheading">(${EWFTotalPriceRange.minEWFTotalPriceWithFreight.toLocaleString('en-US', { maximumFractionDigits: 0 })} - ${EWFTotalPriceRange.maxEWFTotalPriceWithFreight.toLocaleString('en-US', { maximumFractionDigits: 0 })} with freight)</span> */}
                          </td>
                          <td className="action-button-wrapper">
                            <a href={EWF_PRODUCT_URL} target="_blank" className="button button--primary button--small">Shop EWF</a>
                          </td>
                        </tr>
                      </>
                    )}


                  </tbody>
                </table>

              </div>
            )}

            {/* <div className="modal-text">
              <h4 className="heading h4 modal-bottom-text">Why Choose Pour in Place?</h4>
              <p className="modal-text modal-bottom-text">When designing a safe and beautiful play space, understanding the costs of pour-in-place surfacing is crucial for proper planning. With versatile materials and longer-term durability, poured-in-place rubber quickly becomes a smart investment for many playground and facility owners. <a className="link link--primary" href="https://willygoat.com/blogs/resource-center/cost-breakdown-of-pour-in-place-surfacing" target="_blank">Learn More</a></p>
            </div>

            <p className="modal-text modal-bottom-text modal-text-solo"><a className="link link--primary" href="https://willygoat.com/blogs/resource-center/cost-breakdown-of-pour-in-place-surfacing" target="_blank">Learn more about the various surfacing options</a></p>

            */}

            { pipPriceRange && mulchTotalPrice && (
              <>
                <div className="modal-text-wrapper">
                  <p className="modal-text modal-bottom-text modal-text-solo">Request a quote with the shipping address for an exact price.</p>
                  <p className="modal-text modal-bottom-text modal-text-solo"><a href="">Learn more about the various surfacing options</a></p>
                  {/* <a href="" className="button button--outline">Read More</a> */}
                </div>
              </>
            )}

            {/* <div className="modal-text">
              <img className="pip-table-image" src="https://cdn.shopify.com/s/files/1/0100/6072/6369/files/pip-table-cropped.jpg?v=1764697781"></img>
            </div> */}

            {/* <div className="modal-text">
              <h4 className="heading h4 modal-bottom-text">Why Pour-in-Place is the Premium Surfacing Solution:</h4>
              <ul className="modal-bottom-text list-unstyled">
                <li>⭐ Lifetime Value: PIP offers superior durability and minimal maintenance, leading to significant long-term cost savings.</li>
                <li>⭐ Unmatched Safety: 5-star safety ratings, PIP provides excellent shock absorption and a seamless surface.</li>
                <li>⭐ Customization: Tailor colors and patterns to your vision, creating a vibrant and engaging play environment.</li>
                <li>⭐ Superior Accessibility: As an ADA-recommended product, PIP ensures easy navigation for all visitors.</li>
              </ul>
              <p className="modal-text modal-bottom-text"><a className="link link--primary" href="https://willygoat.com/blogs/resource-center/cost-breakdown-of-pour-in-place-surfacing" target="_blank">Learn More</a></p>
            </div> */}


          </div>
        </div>
      </div>
    </div>



  );
};

